import './StreamingFlightPlan.scss';
import cn from 'classnames';
import * as Dfns from 'date-fns/fp';
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  getDay,
  parseISO,
  startOfMonth
} from 'date-fns';
import { Workbook } from 'exceljs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Modal, Overlay, Popover } from 'react-bootstrap';
import { MdOutlineAutoAwesome } from 'react-icons/md';
import { AgentSessionManager } from '@Components/AgentChat';
import { Button } from '@Components/Button';
import { Appearance, Design, Placement, Size } from '@Components/designConstants';
import { FloatingPanel } from '@Components/FloatingPanel';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { DatePicker } from '@Components/DatePicker/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { FilterBar } from '@Components/FilterBar/FilterBar';
import { Page } from '@Components/Page/Page';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { formatMoneyAsInt, formatNumberAsInt } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useAppSelector } from '../../redux';
import { useNetworkMap } from '../../redux/networks';
import { useSetError } from '../../redux/modals';
import * as UserRedux from '../../redux/user';
import { Agent, useAgentEndpoint } from '../../utils/agent-endpoints';
import { getContrastTextColor } from '../../utils/colors';
import { awaitJSON, StreamingOrdersLambdaFetch } from '../../utils/fetch-utils';
import { CANCELED_ORDER_STATUSES } from '../../utils/order-status-utils';
import {
  CHANNEL_OPTIONS,
  Channel,
  ChannelType,
  DerivedGroup,
  FlatFlight,
  Flight,
  LanedFlight,
  MEDIA_PLANNING_SUGGESTED_PROMPTS,
  ORDER_STATUS_LABELS,
  Order,
  OrderSummary,
  SpendDisplay,
  VIEW_MODE_OPTIONS,
  ViewMode,
  ViewModeType,
  channelOf,
  fmtCpm,
  fmtK,
  overlapsRange,
  toDisplayCpm,
  toDisplaySpend
} from './streaming-flight-plan-constants';

const DATE_FORMAT = 'yyyy-MM-dd';
const LANE_HEIGHT = 24;
const BAR_HEIGHT = 18;
const LANE_GAP = 4;
const ROW_PADDING = 4;
const DATE_PICKER_MIN_YEAR = 2010;
const DATE_PICKER_MAX_YEAR = new Date().getFullYear() + 10;

const defaultRange = (): DateRange => {
  const today = new Date();
  return {
    start: Dfns.format(DATE_FORMAT, Dfns.startOfMonth(today)),
    end: Dfns.format(DATE_FORMAT, Dfns.endOfMonth(today))
  };
};

const flightDescription = (f: Flight): string => {
  if (f.useDescription) return f.description || '';
  return [f.content, f.additional].filter(Boolean).join(' / ');
};

// ── Timeline math ────────────────────────────────────────────────────────────

const barGeometry = (
  flightStart: string,
  flightEnd: string,
  rangeStart: Date,
  totalDays: number
): { left: number; width: number } => {
  const fs = parseISO(flightStart);
  const fe = parseISO(flightEnd);
  const startOffset = Math.max(0, differenceInCalendarDays(fs, rangeStart));
  const endOffset = Math.min(totalDays, differenceInCalendarDays(fe, rangeStart) + 1);
  return {
    left: (startOffset / totalDays) * 100,
    width: Math.max(0.3, ((endOffset - startOffset) / totalDays) * 100)
  };
};

// Assign flights to non-overlapping lanes within a group
const assignLanes = (flights: FlatFlight[]): LanedFlight[][] => {
  const sorted = [...flights].sort((a, b) => a.startDate.localeCompare(b.startDate));
  const lanes: LanedFlight[][] = [];
  const laneEnds: string[] = [];

  for (const f of sorted) {
    const idx = laneEnds.findIndex(end => f.startDate > end);
    if (idx === -1) {
      lanes.push([{ ...f, lane: lanes.length }]);
      laneEnds.push(f.endDate);
    } else {
      lanes[idx].push({ ...f, lane: idx });
      laneEnds[idx] = laneEnds[idx] > f.endDate ? laneEnds[idx] : f.endDate;
    }
  }
  return lanes;
};

const apportionShare = (flight: FlatFlight, rangeStart: string, rangeEnd: string): number => {
  const overlapStart = flight.startDate > rangeStart ? flight.startDate : rangeStart;
  const overlapEnd = flight.endDate < rangeEnd ? flight.endDate : rangeEnd;
  if (overlapStart > overlapEnd) return 0;
  const daysInRange = differenceInCalendarDays(parseISO(overlapEnd), parseISO(overlapStart)) + 1;
  const totalDays =
    differenceInCalendarDays(parseISO(flight.endDate), parseISO(flight.startDate)) + 1;
  return daysInRange / totalDays;
};

const apportionedSpend = (flight: FlatFlight, rangeStart: string, rangeEnd: string): number =>
  flight.spend * apportionShare(flight, rangeStart, rangeEnd);

const apportionedImpressions = (flight: FlatFlight, rangeStart: string, rangeEnd: string): number =>
  flight.impressions * apportionShare(flight, rangeStart, rangeEnd);

// ── Data helpers ─────────────────────────────────────────────────────────────

const flattenFlights = (
  orders: Order[],
  networkNameMap: Record<string, string>,
  networkColorMap: Record<string, string>,
  rangeStart: string,
  rangeEnd: string
): FlatFlight[] => {
  const rows: FlatFlight[] = [];
  for (const order of orders) {
    for (const flight of order.flights ?? []) {
      if (flight.canceled) continue;
      if (!overlapsRange(flight.startDate, flight.endDate, rangeStart, rangeEnd)) continue;
      rows.push({
        key: `${order.id}:${flight.id}`,
        orderId: order.id,
        network: order.network,
        networkName: networkNameMap[order.network] ?? order.network,
        orderStatus: order.status,
        statusLabel: ORDER_STATUS_LABELS[order.status] ?? String(order.status),
        platform: flight.platform,
        description: flightDescription(flight),
        content: flight.content ?? '',
        additional: flight.additional ?? '',
        startDate: flight.startDate,
        endDate: flight.endDate,
        spend: flight.spend ?? 0,
        cpm: flight.cpm ?? 0,
        impressions: flight.impressions ?? 0,
        dsp: flight.dsp ?? '',
        canceled: Boolean(flight.canceled),
        derivedNetwork: flight.derivedNetwork || `${order.network} — ${flight.platform}`,
        color: networkColorMap[order.network] ?? '#4a7fc1',
        showTitle: flight.showTitle ?? '',
        spotPosition: flight.spotPosition ?? ''
      });
    }
  }
  return rows;
};

const buildGroups = (
  flights: FlatFlight[],
  rangeStart: string,
  rangeEnd: string,
  display: SpendDisplay
): DerivedGroup[] => {
  const map = new Map<string, FlatFlight[]>();
  for (const f of flights) {
    if (!map.has(f.derivedNetwork)) {
      map.set(f.derivedNetwork, []);
    }
    map.get(f.derivedNetwork)!.push(f);
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([derivedId, groupFlights]) => {
      const first = groupFlights[0];
      const firstContent = groupFlights.find(f => f.content)?.content ?? '';
      const firstAdditional = groupFlights.find(f => f.additional)?.additional ?? '';
      const firstDescription = groupFlights.find(f => f.description)?.description ?? '';
      const firstShowTitle = groupFlights.find(f => f.showTitle)?.showTitle ?? '';
      const firstSpotPosition = groupFlights.find(f => f.spotPosition)?.spotPosition ?? '';
      const totalSpend = groupFlights.reduce(
        (s, f) =>
          s +
          toDisplaySpend(
            apportionedSpend(f, rangeStart, rangeEnd),
            apportionedImpressions(f, rangeStart, rangeEnd),
            display
          ),
        0
      );
      return {
        derivedId,
        network: first.network,
        networkName: first.networkName,
        color: first.color,
        description: firstDescription,
        content: firstContent,
        additional: firstAdditional,
        showTitle: firstShowTitle,
        spotPosition: firstSpotPosition,
        totalSpend,
        totalImpressions: groupFlights.reduce((s, f) => s + f.impressions, 0),
        lanes: assignLanes(groupFlights)
      };
    });
};

// ── Excel export ─────────────────────────────────────────────────────────────

const exportFlightPlan = async (
  flights: FlatFlight[],
  rangeStart: Date,
  rangeEnd: Date,
  rangeStartStr: string,
  rangeEndStr: string,
  display: SpendDisplay
) => {
  const wb = new Workbook();
  const isNet = display.viewMode === ViewMode.NET;
  const spendHeader = isNet ? 'Booked Client Net' : 'Booked Gross Media';
  const apportionedHeader = isNet
    ? `Apportioned Client Net (${rangeStartStr} – ${rangeEndStr})`
    : `Apportioned Gross Media (${rangeStartStr} – ${rangeEndStr})`;
  const cpmHeader = isNet ? 'Client Net CPM' : 'CPM';

  // ── Sheet 1: Flights ──────────────────────────────────────────────────────
  const flightSheet = wb.addWorksheet('Flights');
  flightSheet.columns = [
    { header: 'Derived ID', key: 'derivedNetwork', width: 32 },
    { header: 'Network', key: 'network', width: 14 },
    { header: 'Network Name', key: 'networkName', width: 26 },
    { header: 'Description', key: 'description', width: 40 },
    { header: 'Show Title', key: 'showTitle', width: 28 },
    { header: 'Spot Position', key: 'spotPosition', width: 16 },
    { header: 'Platform', key: 'platform', width: 18 },
    { header: 'Start Date', key: 'startDate', width: 12 },
    { header: 'End Date', key: 'endDate', width: 12 },
    { header: 'Order ID', key: 'orderId', width: 10 },
    { header: 'Status', key: 'orderStatus', width: 12 },
    { header: spendHeader, key: 'spend', width: 18 },
    { header: apportionedHeader, key: 'apportioned', width: 32 },
    { header: 'Impressions', key: 'impressions', width: 14 },
    { header: cpmHeader, key: 'cpm', width: 14 },
    { header: 'DSP', key: 'dsp', width: 18 }
  ];
  flightSheet.getRow(1).font = { bold: true };

  for (const f of flights) {
    flightSheet.addRow({
      derivedNetwork: f.derivedNetwork,
      network: f.network,
      networkName: f.networkName,
      description: f.description,
      showTitle: f.showTitle,
      spotPosition: f.spotPosition,
      platform: f.platform,
      startDate: f.startDate,
      endDate: f.endDate,
      orderId: f.orderId,
      orderStatus: ORDER_STATUS_LABELS[f.orderStatus] ?? f.orderStatus,
      spend: toDisplaySpend(f.spend, f.impressions, display),
      apportioned: toDisplaySpend(
        apportionedSpend(f, rangeStartStr, rangeEndStr),
        apportionedImpressions(f, rangeStartStr, rangeEndStr),
        display
      ),
      impressions: f.impressions,
      cpm: toDisplayCpm(f.cpm, display),
      dsp: f.dsp
    });
  }

  ['spend', 'apportioned'].forEach(k => {
    flightSheet.getColumn(k).numFmt = '$#,##0';
  });
  flightSheet.getColumn('cpm').numFmt = '$#,##0.00';
  flightSheet.getColumn('impressions').numFmt = '#,##0';

  // ── Sheet 2: Weekly Summary ───────────────────────────────────────────────
  const totalDays = differenceInCalendarDays(rangeEnd, rangeStart) + 1;
  const weeks = computeWeekSpends(flights, rangeStart, rangeEnd, totalDays, display);

  const weekSheet = wb.addWorksheet('Weekly Summary');
  weekSheet.columns = [
    { header: 'Week Starting', key: 'week', width: 14 },
    {
      header: isNet ? 'Apportioned Client Net' : 'Apportioned Gross Media',
      key: 'spend',
      width: 24
    }
  ];
  weekSheet.getRow(1).font = { bold: true };
  weekSheet.getColumn('spend').numFmt = '$#,##0';

  for (const w of weeks) {
    weekSheet.addRow({ week: w.key, spend: w.spend });
  }

  // ── Download ──────────────────────────────────────────────────────────────
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `flight-plan-${rangeStartStr}-to-${rangeEndStr}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};

// ── Sub-components ───────────────────────────────────────────────────────────

interface SummaryBarProps {
  flights: FlatFlight[];
  rangeStart: string;
  rangeEnd: string;
  display: SpendDisplay;
  lastBookedDate: string;
  loading: boolean;
  loadedCount: number;
  totalCount: number;
}

const SummaryBar = ({
  flights,
  rangeStart,
  rangeEnd,
  display,
  lastBookedDate,
  loading,
  loadedCount,
  totalCount
}: SummaryBarProps) => {
  const totalDisplay = flights.reduce(
    (s, f) =>
      s +
      toDisplaySpend(
        apportionedSpend(f, rangeStart, rangeEnd),
        apportionedImpressions(f, rangeStart, rangeEnd),
        display
      ),
    0
  );
  const totalImpressions = flights.reduce(
    (s, f) => s + apportionedImpressions(f, rangeStart, rangeEnd),
    0
  );
  const avgCpm = totalImpressions > 0 ? (totalDisplay / totalImpressions) * 1000 : 0;
  const isNet = display.viewMode === ViewMode.NET;
  return (
    <div className='sfp-summary-bar'>
      <div className='sfp-summary-stat'>
        <span className='sfp-summary-label'>Flights</span>
        <span className='sfp-summary-value'>{flights.length.toLocaleString()}</span>
      </div>
      <div className='sfp-summary-stat'>
        <span className='sfp-summary-label'>
          {isNet ? 'Total Client Net' : 'Total Gross Media'}
        </span>
        <span className='sfp-summary-value'>{formatMoneyAsInt(totalDisplay)}</span>
      </div>
      <div className='sfp-summary-stat'>
        <span className='sfp-summary-label'>Total Impressions</span>
        <span className='sfp-summary-value'>{formatNumberAsInt(totalImpressions)}</span>
      </div>
      <div className='sfp-summary-stat'>
        <span className='sfp-summary-label'>{isNet ? 'Avg Client Net CPM' : 'Avg CPM'}</span>
        <span className='sfp-summary-value'>{fmtCpm(avgCpm)}</span>
      </div>
      <div className='sfp-summary-stat'>
        <span className='sfp-summary-label'>Last Booked</span>
        <span className='sfp-summary-value'>
          {lastBookedDate ? Dfns.format('MMM d, yyyy', parseISO(lastBookedDate)) : '—'}
        </span>
      </div>
      {loading && totalCount > 0 && (
        <div className='sfp-summary-progress'>
          Loading orders… {loadedCount} / {totalCount}
        </div>
      )}
    </div>
  );
};

// ── Gantt components ─────────────────────────────────────────────────────────

interface FlightBarProps {
  flight: LanedFlight;
  rangeStart: Date;
  totalDays: number;
  laneIndex: number;
  cid: string | undefined;
  display: SpendDisplay;
}

const POPOVER_CLOSE_DELAY_MS = 150;

const FlightBar = ({ flight, rangeStart, totalDays, laneIndex, cid, display }: FlightBarProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [show, setShow] = useState(false);
  const { left, width } = barGeometry(flight.startDate, flight.endDate, rangeStart, totalDays);
  const top = ROW_PADDING + laneIndex * (LANE_HEIGHT + LANE_GAP) + (LANE_HEIGHT - BAR_HEIGHT) / 2;

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setShow(false), POPOVER_CLOSE_DELAY_MS);
  };
  useEffect(() => () => cancelClose(), []);

  return (
    <>
      <div
        ref={ref}
        className='gantt-bar'
        style={{
          left: `calc(${left}% + 3px)`,
          width: `calc(${width}% - 6px)`,
          top,
          height: BAR_HEIGHT,
          backgroundColor: flight.color
        }}
        onMouseEnter={() => {
          cancelClose();
          setShow(true);
        }}
        onMouseLeave={scheduleClose}
      >
        <span className='gantt-bar-label' style={{ color: getContrastTextColor(flight.color) }}>
          {formatMoneyAsInt(toDisplaySpend(flight.spend, flight.impressions, display))} ·{' '}
          {fmtK(flight.impressions)} · CPM {fmtCpm(toDisplayCpm(flight.cpm, display))}
        </span>
      </div>
      <Overlay target={ref} show={show} placement={Placement.TOP}>
        <Popover
          id={`tip-${flight.key}`}
          className='sfp-flight-popover'
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <Popover.Body>
            <FlightTooltipContent flight={flight} cid={cid} display={display} />
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
};

interface FlightTooltipContentProps {
  flight: LanedFlight;
  cid: string | undefined;
  display: SpendDisplay;
}

const FlightTooltipContent = ({ flight, cid, display }: FlightTooltipContentProps) => (
  <div className='sfp-tooltip'>
    <div className='sfp-tooltip-title'>{flight.derivedNetwork}</div>
    <div className='sfp-tooltip-row'>
      <span>Network</span>
      <span>
        {flight.network} — {flight.networkName}
      </span>
    </div>
    <div className='sfp-tooltip-row'>
      <span>Order</span>
      <span>
        {cid ? (
          <a
            className='sfp-tooltip-link'
            href={`/${cid}/streaming/buying/${flight.network}/${flight.orderId}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            #{flight.orderId} ↗
          </a>
        ) : (
          <>#{flight.orderId}</>
        )}{' '}
        ({ORDER_STATUS_LABELS[flight.orderStatus] ?? flight.orderStatus})
      </span>
    </div>
    <div className='sfp-tooltip-row'>
      <span>Platform</span>
      <span>{flight.platform}</span>
    </div>
    {flight.description && (
      <div className='sfp-tooltip-row'>
        <span>Description</span>
        <span>{flight.description}</span>
      </div>
    )}
    {flight.showTitle && (
      <div className='sfp-tooltip-row'>
        <span>Show</span>
        <span>{flight.showTitle}</span>
      </div>
    )}
    {flight.spotPosition && (
      <div className='sfp-tooltip-row'>
        <span>Spot Position</span>
        <span>{flight.spotPosition}</span>
      </div>
    )}
    <div className='sfp-tooltip-row'>
      <span>Dates</span>
      <span>
        {flight.startDate} → {flight.endDate}
      </span>
    </div>
    <div className='sfp-tooltip-divider' />
    <div className='sfp-tooltip-row'>
      <span>{display.viewMode === ViewMode.NET ? 'Client Net' : 'Gross Media'}</span>
      <span>{formatMoneyAsInt(toDisplaySpend(flight.spend, flight.impressions, display))}</span>
    </div>
    <div className='sfp-tooltip-row'>
      <span>{display.viewMode === ViewMode.NET ? 'Client Net CPM' : 'CPM'}</span>
      <span>{fmtCpm(toDisplayCpm(flight.cpm, display))}</span>
    </div>
    <div className='sfp-tooltip-row'>
      <span>Impressions</span>
      <span>{formatNumberAsInt(flight.impressions)}</span>
    </div>
    {flight.dsp && (
      <div className='sfp-tooltip-row'>
        <span>DSP</span>
        <span>{flight.dsp}</span>
      </div>
    )}
  </div>
);

interface GanttRowProps {
  group: DerivedGroup;
  rangeStart: Date;
  totalDays: number;
  cid: string | undefined;
  display: SpendDisplay;
}

const GanttRow = ({ group, rangeStart, totalDays, cid, display }: GanttRowProps) => {
  const laneCount = group.lanes.length;
  const rowHeight = ROW_PADDING * 2 + laneCount * LANE_HEIGHT + (laneCount - 1) * LANE_GAP;

  const contentLine =
    group.content || group.additional
      ? [group.content, group.additional].filter(Boolean).join(' / ')
      : group.description;
  const podcastLine = [group.showTitle, group.spotPosition].filter(Boolean).join(' · ');

  return (
    <div className='gantt-row' style={{ minHeight: rowHeight }}>
      <div
        className='gantt-label-col'
        title={[group.derivedId, contentLine, podcastLine].filter(Boolean).join('\n')}
      >
        <span className='gantt-row-network'>{group.networkName}</span>
        {contentLine && <span className='gantt-row-content'>{contentLine}</span>}
        {podcastLine && <span className='gantt-row-content'>{podcastLine}</span>}
        <span className='gantt-row-spend'>{formatMoneyAsInt(group.totalSpend)}</span>
      </div>
      <div className='gantt-timeline-col' style={{ minHeight: rowHeight }}>
        {group.lanes.flatMap((lane, laneIndex) =>
          lane.map(flight => (
            <FlightBar
              key={flight.key}
              flight={flight}
              rangeStart={rangeStart}
              totalDays={totalDays}
              laneIndex={laneIndex}
              cid={cid}
              display={display}
            />
          ))
        )}
      </div>
    </div>
  );
};

interface TimelineTicksProps {
  rangeStart: Date;
  rangeEnd: Date;
  totalDays: number;
}

const TimelineTicks = ({ rangeStart, rangeEnd, totalDays }: TimelineTicksProps) => {
  const ticks = useMemo(() => {
    const result: { label: string; left: number; isMonth: boolean }[] = [];

    // Month-start ticks
    const monthStarts = new Set<string>();
    let cur = startOfMonth(rangeStart);
    while (cur <= rangeEnd) {
      monthStarts.add(Dfns.format('yyyy-MM-dd', cur));
      const offset = differenceInCalendarDays(cur, rangeStart);
      result.push({
        label: Dfns.format('MMM d', cur),
        left: (Math.max(0, offset) / totalDays) * 100,
        isMonth: true
      });
      cur = addMonths(cur, 1);
    }

    // Monday ticks (skip if already covered by a month-start)
    const dayOfWeek = getDay(rangeStart); // 0=Sun, 1=Mon
    const daysUntilMonday = dayOfWeek === 1 ? 0 : (8 - dayOfWeek) % 7;
    let monday = addDays(rangeStart, daysUntilMonday);
    while (monday <= rangeEnd) {
      const key = Dfns.format('yyyy-MM-dd', monday);
      if (!monthStarts.has(key)) {
        const offset = differenceInCalendarDays(monday, rangeStart);
        result.push({
          label: Dfns.format('MMM d', monday),
          left: (offset / totalDays) * 100,
          isMonth: false
        });
      }
      monday = addDays(monday, 7);
    }

    return result.sort((a, b) => a.left - b.left);
  }, [rangeStart, rangeEnd, totalDays]);

  return (
    <>
      {ticks.map(tick => (
        <div
          key={tick.label}
          className={tick.isMonth ? 'gantt-tick gantt-tick-month' : 'gantt-tick'}
          style={{ left: `${tick.left}%` }}
        >
          {tick.label}
        </div>
      ))}
    </>
  );
};

interface WeekSpend {
  key: string;
  left: number;
  width: number;
  spend: number;
}

const computeWeekSpends = (
  flights: FlatFlight[],
  rangeStart: Date,
  rangeEnd: Date,
  totalDays: number,
  display: SpendDisplay
): WeekSpend[] => {
  const rangeStartStr = Dfns.format('yyyy-MM-dd', rangeStart);
  const rangeEndStr = Dfns.format('yyyy-MM-dd', rangeEnd);

  const dow = getDay(rangeStart);
  const daysBack = dow === 0 ? 6 : dow - 1;
  let weekStart = addDays(rangeStart, -daysBack);
  const weeks: WeekSpend[] = [];

  while (weekStart <= rangeEnd) {
    const weekEnd = addDays(weekStart, 6);
    const wsStr = Dfns.format('yyyy-MM-dd', weekStart);
    const weStr = Dfns.format('yyyy-MM-dd', weekEnd);

    // Clamp week window to the date range so partial weeks at the edges don't
    // count days outside the range that the network totals don't count either.
    const effectiveStart = wsStr > rangeStartStr ? wsStr : rangeStartStr;
    const effectiveEnd = weStr < rangeEndStr ? weStr : rangeEndStr;

    const spend = flights.reduce((sum, f) => {
      const overlapStart = f.startDate > effectiveStart ? f.startDate : effectiveStart;
      const overlapEnd = f.endDate < effectiveEnd ? f.endDate : effectiveEnd;
      if (overlapStart > overlapEnd) return sum;
      const daysInWeek = differenceInCalendarDays(parseISO(overlapEnd), parseISO(overlapStart)) + 1;
      const totalFlightDays =
        differenceInCalendarDays(parseISO(f.endDate), parseISO(f.startDate)) + 1;
      const share = daysInWeek / totalFlightDays;
      return sum + toDisplaySpend(f.spend * share, f.impressions * share, display);
    }, 0);

    const visStart = Math.max(0, differenceInCalendarDays(weekStart, rangeStart));
    const visEnd = Math.min(totalDays, differenceInCalendarDays(weekEnd, rangeStart) + 1);

    if (spend > 0) {
      weeks.push({
        key: wsStr,
        left: (visStart / totalDays) * 100,
        width: ((visEnd - visStart) / totalDays) * 100,
        spend
      });
    }

    weekStart = addDays(weekStart, 7);
  }

  return weeks;
};

interface WeekSpendTicksProps {
  flights: FlatFlight[];
  rangeStart: Date;
  rangeEnd: Date;
  totalDays: number;
  display: SpendDisplay;
}

const WeekSpendTicks = ({
  flights,
  rangeStart,
  rangeEnd,
  totalDays,
  display
}: WeekSpendTicksProps) => {
  const weeks = useMemo(
    () => computeWeekSpends(flights, rangeStart, rangeEnd, totalDays, display),
    [flights, rangeStart, rangeEnd, totalDays, display]
  );

  return (
    <>
      {weeks.map(w => (
        <div
          key={w.key}
          className='gantt-week-spend'
          style={{ left: `${w.left}%`, width: `${w.width}%` }}
        >
          {formatMoneyAsInt(w.spend)}
        </div>
      ))}
    </>
  );
};

interface GanttViewProps {
  groups: DerivedGroup[];
  flights: FlatFlight[];
  dateRange: DateRange;
  cid: string | undefined;
  display: SpendDisplay;
}

const GanttView = ({ groups, flights, dateRange, cid, display }: GanttViewProps) => {
  const rangeStart = parseISO(dateRange.start);
  const rangeEnd = parseISO(dateRange.end);
  const totalDays = differenceInCalendarDays(rangeEnd, rangeStart) + 1;

  if (groups.length === 0) {
    return <div className='gantt-empty'>No flights found for the selected filters.</div>;
  }

  return (
    <div className='gantt-container'>
      <div className='gantt-header-row'>
        <div className='gantt-label-col gantt-label-header' />
        <div className='gantt-timeline-col gantt-timeline-header'>
          <div className='gantt-ticks-row'>
            <TimelineTicks rangeStart={rangeStart} rangeEnd={rangeEnd} totalDays={totalDays} />
          </div>
          <div className='gantt-ticks-row gantt-week-spend-row'>
            <WeekSpendTicks
              flights={flights}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              totalDays={totalDays}
              display={display}
            />
          </div>
        </div>
      </div>
      <div className='gantt-body'>
        {groups.map(group => (
          <GanttRow
            key={group.derivedId}
            group={group}
            rangeStart={rangeStart}
            totalDays={totalDays}
            cid={cid}
            display={display}
          />
        ))}
      </div>
    </div>
  );
};

// ── Root page ────────────────────────────────────────────────────────────────

export const StreamingFlightPlan = () => {
  const { cid: company, commission: companyCommission } = useCompanyInfo();
  const networkMap = useNetworkMap();
  const setError = useSetError();
  const mediaPlanningAgentEndpoint = useAgentEndpoint(Agent.MEDIA_PLANNING);

  const [dateRange, setDateRange] = useState<DateRange>(defaultRange);
  const [orderSummaries, setOrderSummaries] = useState<OrderSummary[] | null>(null);
  const [loadedOrders, setLoadedOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [flightFilter, setFlightFilter] = useState<(f: FlatFlight) => boolean>(() => () => true);
  const [selectedChannel, setSelectedChannel] = useState<ChannelType>(Channel.ALL);
  const [viewMode, setViewMode] = useState<ViewModeType>(ViewMode.GROSS);
  const [commission, setCommission] = useState<number>(0);
  const [commissionModalOpen, setCommissionModalOpen] = useState(false);
  const [commissionDraft, setCommissionDraft] = useState<string>('');

  const display = useMemo<SpendDisplay>(() => ({ viewMode, commission }), [viewMode, commission]);

  const openCommissionModal = () => {
    const seedDecimal = commission || companyCommission || 0;
    setCommissionDraft(seedDecimal ? String(seedDecimal * 100) : '');
    setCommissionModalOpen(true);
  };

  const handleViewModeChange = (next: ViewModeType) => {
    if (next === ViewMode.NET) {
      openCommissionModal();
      return;
    }
    setViewMode(next);
  };

  const applyCommission = () => {
    const parsedPercent = parseFloat(commissionDraft);
    const nextDecimal = Number.isFinite(parsedPercent) ? Math.max(0, parsedPercent) / 100 : 0;
    setCommission(nextDecimal);
    setViewMode(ViewMode.NET);
    setCommissionModalOpen(false);
  };

  // Fetch the lightweight order index once per company
  useEffect(() => {
    if (!company) return () => {};
    let cancelled = false;
    (async () => {
      setOrderSummaries(null);
      try {
        const res = await StreamingOrdersLambdaFetch('/order_info', { params: { company } });
        const data: OrderSummary[] = await awaitJSON(res);
        if (!cancelled) {
          setOrderSummaries(data);
        }
      } catch (e) {
        if (!cancelled) {
          setError({ message: `Failed to load order index: ${e.message}`, reportError: e });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [company, setError]);

  // Fetch full order details for orders overlapping the selected date range
  useEffect(() => {
    if (!orderSummaries || !dateRange.start || !dateRange.end) return () => {};
    let cancelled = false;

    const inRange = orderSummaries.filter(o =>
      overlapsRange(o.start, o.end, dateRange.start, dateRange.end)
    );

    (async () => {
      setLoadedOrders([]);
      setLoadedCount(0);
      setTotalCount(inRange.length);
      setLoading(inRange.length > 0);

      if (inRange.length === 0) return;

      const results: Order[] = [];
      await Promise.all(
        inRange.map(async summary => {
          try {
            const res = await StreamingOrdersLambdaFetch('/order', { params: { id: summary.id } });
            const order: Order = await awaitJSON(res);
            if (!cancelled) {
              results.push(order);
              setLoadedCount(n => n + 1);
            }
          } catch {
            // skip orders that fail individually rather than aborting the batch
          }
        })
      );
      if (!cancelled) {
        setLoadedOrders(results);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orderSummaries, dateRange]);

  const networkNameMap = useMemo(
    () =>
      networkMap ? Object.fromEntries(Object.entries(networkMap).map(([k, v]) => [k, v.name])) : {},
    [networkMap]
  );

  const networkColorMap = useMemo(
    () =>
      networkMap
        ? Object.fromEntries(Object.entries(networkMap).map(([k, v]) => [k, v.color]))
        : {},
    [networkMap]
  );

  const allFlights = useMemo(
    () =>
      flattenFlights(loadedOrders, networkNameMap, networkColorMap, dateRange.start, dateRange.end),
    [loadedOrders, networkNameMap, networkColorMap, dateRange]
  );

  const filteredFlights = useMemo(
    () =>
      allFlights.filter(
        f =>
          flightFilter(f) &&
          (selectedChannel === Channel.ALL || channelOf(f.platform) === selectedChannel)
      ),
    [allFlights, flightFilter, selectedChannel]
  );

  const groups = useMemo(
    () => buildGroups(filteredFlights, dateRange.start, dateRange.end, display),
    [filteredFlights, dateRange, display]
  );

  const lastBookedDate = useMemo(
    () =>
      filteredFlights
        .filter(f => !CANCELED_ORDER_STATUSES.has(f.orderStatus) && f.impressions > 0)
        .reduce((max, f) => (f.endDate > max ? f.endDate : max), ''),
    [filteredFlights]
  );

  const isInitialLoad = orderSummaries === null;
  const [agentChatOpen, setAgentChatOpen] = useState(false);
  const [agentSidePanelOpen, setAgentSidePanelOpen] = useState(false);

  return (
    <Page title={PAGE_NAMES.STREAMING_FLIGHT_PLAN} pageType={PAGE_NAMES.STREAMING_FLIGHT_PLAN}>
      <div className='streaming-flight-plan'>
        <div className='sfp-controls'>
          <div className='sfp-controls-row'>
            <SegmentedControlButton
              size={Size.LG}
              options={CHANNEL_OPTIONS}
              selectedOption={selectedChannel}
              onChange={setSelectedChannel}
            />
            <SegmentedControlButton
              size={Size.LG}
              options={VIEW_MODE_OPTIONS}
              selectedOption={viewMode}
              onChange={handleViewModeChange}
            />
            {viewMode === ViewMode.NET && (
              <Button
                size={Size.LG}
                design={Design.NEUTRAL}
                appearance={Appearance.OUTLINED}
                onClick={openCommissionModal}
              >
                {`Commission: ${(commission * 100).toFixed(2).replace(/\.?0+$/, '')}% · Edit`}
              </Button>
            )}
          </div>
          <div className='sfp-controls-row'>
            <DatePicker
              range={dateRange}
              onChange={useCallback((newRange: DateRange) => setDateRange(newRange), [])}
              datePresets
              isOutsideRange={() => false}
              minYear={DATE_PICKER_MIN_YEAR}
              maxYear={DATE_PICKER_MAX_YEAR}
            />
            <FilterBar
              size={Size.LG}
              className='sfp-filter-bar'
              hasAdvanced
              defaultAdvanced
              lines={allFlights}
              options={['network', 'networkName', 'platform', 'statusLabel']}
              onFilter={f => setFlightFilter(() => f)}
              useLocalStorage={false}
              placeholder='Filter by network, platform, status…'
            />
            <Button
              size={Size.LG}
              onClick={() =>
                exportFlightPlan(
                  filteredFlights,
                  parseISO(dateRange.start),
                  parseISO(dateRange.end),
                  dateRange.start,
                  dateRange.end,
                  display
                )
              }
            >
              Export
            </Button>
          </div>
        </div>

        <SummaryBar
          flights={filteredFlights}
          rangeStart={dateRange.start}
          rangeEnd={dateRange.end}
          display={display}
          lastBookedDate={lastBookedDate}
          loading={loading}
          loadedCount={loadedCount}
          totalCount={totalCount}
        />

        <div className='sfp-gantt-wrapper'>
          {isInitialLoad || loading ? (
            <FullPageSpinner />
          ) : (
            <GanttView
              groups={groups}
              flights={filteredFlights}
              dateRange={dateRange}
              cid={company}
              display={display}
            />
          )}
        </div>

        <InteractiveDiv
          className={cn('sfp-agent-fab', { hidden: agentChatOpen })}
          onClick={() => setAgentChatOpen(true)}
          aria-label='Open Media Planning Agent'
        >
          <MdOutlineAutoAwesome />
        </InteractiveDiv>

        <FloatingPanel
          id='media-planning-agent-panel'
          show={agentChatOpen}
          onHide={() => setAgentChatOpen(false)}
          showSidePanel={agentSidePanelOpen}
          setShowSidePanel={setAgentSidePanelOpen}
          leftActions={<span className='sfp-agent-panel-title'>Media Planning Agent</span>}
          minWidth={342}
          maxWidth={1440}
          initialWidth={465}
          minHeight={396}
          initialHeight={745}
          initialPosition={{ xDist: 32, xDirection: 'right', yDist: 32, yDirection: 'bottom' }}
          body={
            <AgentSessionManager
              agentEndpoint={mediaPlanningAgentEndpoint}
              agent={Agent.MEDIA_PLANNING}
              company={company}
              emptyState={<MediaPlanningEmptyState />}
              suggestedPrompts={MEDIA_PLANNING_SUGGESTED_PROMPTS}
              visible={agentChatOpen}
            />
          }
        />
      </div>
      <CommissionModal
        show={commissionModalOpen}
        draft={commissionDraft}
        onDraftChange={setCommissionDraft}
        onCancel={() => setCommissionModalOpen(false)}
        onApply={applyCommission}
      />
    </Page>
  );
};

const MediaPlanningEmptyState = () => {
  const fullName = useAppSelector(UserRedux.fullNameSelector);
  const firstName = fullName?.split(/\s+/)[0] ?? '';

  return (
    <div className='sfp-agent-empty'>
      <div className='sfp-agent-empty-icon'>
        <MdOutlineAutoAwesome />
      </div>
      <div className='sfp-agent-empty-text'>
        <h1>
          {firstName && (
            <>
              <span className='greeting'>Hey, {firstName}!</span>
              <br />
            </>
          )}
          <span className='cta'>Let&apos;s plan your media.</span>
        </h1>
        <p className='sfp-agent-scope-note'>
          I provide channel-level budget recommendations across all TVAD channels, with detailed
          line-item recommendations for streaming, audio, and display.
        </p>
        <div className='sfp-agent-governance'>
          <p className='sfp-agent-governance-lead'>
            I can also <strong>draft insertion orders</strong> for the approved networks — each one
            is a draft held for your review on the Agent Order Approvals screen. Nothing is ever
            sent to a network automatically.
          </p>
          <ul className='sfp-agent-governance-list'>
            <li>
              Every order is a <strong>draft held for human approval</strong> — the agent has no
              send capability.
            </li>
            <li>
              <strong>Separation of duties:</strong> the agent can draft but is barred, in code,
              from committing or sending its own orders — a different person must.
            </li>
            <li>
              <strong>Spend guardrails:</strong> per-line and per-plan ceilings and an
              approved-network allow-list are enforced before any order is created.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// ── Commission modal ─────────────────────────────────────────────────────────

interface CommissionModalProps {
  show: boolean;
  draft: string;
  onDraftChange: (value: string) => void;
  onCancel: () => void;
  onApply: () => void;
}

const CommissionModal = ({
  show,
  draft,
  onDraftChange,
  onCancel,
  onApply
}: CommissionModalProps) => (
  <Modal show={show} onHide={onCancel} centered>
    <Modal.Header closeButton>
      <Modal.Title>Client Net View</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>Client Net amounts are calculated per flight as:</p>
      <pre className='sfp-commission-formula'>
        media × (1 + commission) + $0.19 × impressions/1000 + $0.08 × impressions/1000
      </pre>
      <p>
        The $0.19 ad serving fee and $0.08 identity resolution fee are billed on a CPM basis. Enter
        the commission as a percentage (e.g. 15 for 15%).
      </p>
      <Form.Group>
        <Form.Label>Client Commission %</Form.Label>
        <Form.Control
          autoFocus
          type='number'
          step={0.1}
          min={0}
          value={draft}
          onChange={e => onDraftChange(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              onApply();
            }
          }}
          placeholder='0'
        />
      </Form.Group>
    </Modal.Body>
    <Modal.Footer>
      <Button design={Design.NEUTRAL} appearance={Appearance.OUTLINED} onClick={onCancel}>
        Cancel
      </Button>
      <Button onClick={onApply}>Apply</Button>
    </Modal.Footer>
  </Modal>
);
